import { AxiosResponse } from 'axios';
import {
  buildGovbrAuthHeaders,
  formatAssinaturaGovbrError,
  getSsoTokenValidationMessage,
} from '../utils/ssoToken';
import { apiforFlow } from './api';

export type PrepararAssinaturaResponse = {
  documento_resposta_id: string;
  titulo: string;
  documento_base64: string;
  authorization_url: string;
  redirect_to: string;
  client_id: string;
  controlador_assinar_url: string;
  page: number;
  x: number;
  y: number;
};

export type AssinarGovbrResponse = {
  success: boolean;
  message: string;
  documento_assinado_base64?: string;
  detail?: string;
  trace_id?: string;
  controlador_status?: number;
};

const ensureSessaoValidaParaAssinatura = (): void => {
  const validationMessage = getSsoTokenValidationMessage();
  if (validationMessage) {
    throw new Error(validationMessage);
  }
};

export const prepararAssinaturaDocumento = (
  documentoId: string,
): Promise<AxiosResponse<PrepararAssinaturaResponse>> => {
  ensureSessaoValidaParaAssinatura();
  return apiforFlow.post(
    `flow/documentos-resposta/${documentoId}/preparar-assinatura-govbr/`,
    {},
    {
      headers: buildGovbrAuthHeaders(),
    },
  );
};

export const assinarDocumentoGovbr = (
  documentoId: string,
  code: string,
): Promise<AxiosResponse<AssinarGovbrResponse>> => {
  ensureSessaoValidaParaAssinatura();
  return apiforFlow.post(
    `flow/documentos-resposta/${documentoId}/assinar-govbr/`,
    { code },
    {
      headers: buildGovbrAuthHeaders(),
    },
  );
};

export const confirmarAssinaturaDocumento = (
  documentoId: string,
  documentoAssinadoBase64: string,
): Promise<AxiosResponse> => {
  ensureSessaoValidaParaAssinatura();
  return apiforFlow.post(
    `flow/documentos-resposta/${documentoId}/confirmar-assinatura-govbr/`,
    {
      documento_assinado_base64: documentoAssinadoBase64,
    },
    {
      headers: buildGovbrAuthHeaders(),
    },
  );
};

export const abrirAutorizacaoGovbr = (authorizationUrl: string): Window | null => {
  const width = 600;
  const height = 700;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;
  return window.open(
    authorizationUrl,
    'govbr_assinatura',
    `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`,
  );
};

export const aguardarAutorizacaoGovbr = (): Promise<{ code?: string; error?: string }> =>
  new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      window.removeEventListener('message', onMessage);
      reject(new Error('Tempo esgotado aguardando autorização Gov.br.'));
    }, 300000);

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      const data = event.data;
      if (!data || typeof data !== 'object') {
        return;
      }
      if (data.type === 'ASSINATURA_GOVBR_AUTORIZADO') {
        window.clearTimeout(timeout);
        window.removeEventListener('message', onMessage);
        resolve({ code: data.code });
      }
      if (data.type === 'ASSINATURA_GOVBR_ERROR') {
        window.clearTimeout(timeout);
        window.removeEventListener('message', onMessage);
        reject(new Error(data.error || 'Erro na autorização Gov.br.'));
      }
    };

    window.addEventListener('message', onMessage);
  });

export { formatAssinaturaGovbrError, getSsoTokenValidationMessage };
